import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { getMarketIndices } from "@/components/forecast.service";
import { MarketIndexSeries } from "@/types";
import "@/pages/index/MarketIndex.scss";

const SERIES_LABELS: Record<string, string> = {
	"OSR-GLOBAL-CARBON-SCRAP-IDX": "Global",
	"OSR-CARBON-SCRAP-IDX-East-Asia": "East Asia",
	"OSR-CARBON-SCRAP-IDX-Europe": "Europe",
	"OSR-CARBON-SCRAP-IDX-Middle-East-North-Africa": "Middle East & North Africa",
	"OSR-CARBON-SCRAP-IDX-North-America": "North America",
	"OSR-CARBON-SCRAP-IDX-South-Asia": "South Asia",
};

const SERIES_COLORS = ["#2563eb", "#16a34a", "#dc2626", "#9333ea", "#ea580c", "#0891b2"];
const CHART_WIDTH = 920;
const CHART_HEIGHT = 280;
const CHART_PADDING = 28;

const formatDate = (date: string) => dayjs(date).format("DD.MM.YYYY");
const formatValue = (value: number) => value.toFixed(1);
const formatChange = (value?: number | null) => (value == null ? "-" : `${value > 0 ? "+" : ""}${value.toFixed(1)}%`);
const getChangeClass = (value?: number | null) => {
	if (value == null || value === 0) return "neutral";
	return value > 0 ? "positive" : "negative";
};
const getLabel = (name: string) => SERIES_LABELS[name] || name;

const buildPath = (points: { x: number; y: number }[]) => {
	return points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`).join(" ");
};

export const MarketIndex = () => {
	const [series, setSeries] = useState<MarketIndexSeries[]>([]);
	const [selectedName, setSelectedName] = useState("OSR-GLOBAL-CARBON-SCRAP-IDX");
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setIsLoading(true);
		getMarketIndices()
			.then((data) => {
				setSeries(data);
				if (data.length && !data.some((item) => item.name === selectedName)) {
					setSelectedName(data[0].name);
				}
			})
			.catch(() => setError("Market index data is not available."))
			.finally(() => setIsLoading(false));
	}, []);

	const selectedSeries = useMemo(() => {
		return series.find((item) => item.name === selectedName) || series[0];
	}, [selectedName, series]);

	const chartData = useMemo(() => {
		const points = selectedSeries?.points || [];
		if (!points.length) return null;

		const values = points.map((point) => point.value);
		const min = Math.min(...values);
		const max = Math.max(...values);
		const range = max - min || 1;
		const xStep = points.length > 1 ? (CHART_WIDTH - CHART_PADDING * 2) / (points.length - 1) : 0;
		const scaled = points.map((point, index) => ({
			x: Math.round(CHART_PADDING + index * xStep),
			y: Math.round(CHART_HEIGHT - CHART_PADDING - ((point.value - min) / range) * (CHART_HEIGHT - CHART_PADDING * 2)),
		}));

		return {
			min,
			max,
			path: buildPath(scaled),
			firstDate: points[0].date,
			lastDate: points[points.length - 1].date,
		};
	}, [selectedSeries]);

	const latestRows = useMemo(() => {
		return series
			.map((item) => ({ ...item, latest: item.points[item.points.length - 1] }))
			.filter((item) => item.latest);
	}, [series]);

	if (isLoading) return <div className="forecast-detail">Loading market index...</div>;
	if (error) return <div className="forecast-detail">{error}</div>;
	if (!selectedSeries || !chartData) return <div className="forecast-detail">No market index data found.</div>;

	const selectedColor = SERIES_COLORS[Math.max(series.findIndex((item) => item.name === selectedSeries.name), 0) % SERIES_COLORS.length];
	const selectedLatest = selectedSeries.latest_value ?? selectedSeries.points[selectedSeries.points.length - 1]?.value;
	const selectedLatestDate = selectedSeries.latest_date ?? selectedSeries.points[selectedSeries.points.length - 1]?.date;

	return (
		<div className="forecast-detail market-index">
			<div className="market-index-header">
				<div>
					<p>
						<strong>Scrap metal market index</strong>
					</p>
					<p className="market-index-description">{selectedSeries.description}</p>
				</div>
				<select
					className="form-select market-index-select"
					value={selectedName}
					onChange={(event) => setSelectedName(event.target.value)}
				>
					{series.map((item) => (
						<option key={item.name} value={item.name}>
							{getLabel(item.name)}
						</option>
					))}
				</select>
			</div>

			<div className="market-index-summary" aria-label="Selected market index summary">
				<div>
					<span>Current</span>
					<strong>{selectedLatest == null ? "-" : formatValue(selectedLatest)}</strong>
					<small>{selectedLatestDate ? formatDate(selectedLatestDate) : "-"}</small>
				</div>
				<div>
					<span>1W</span>
					<strong className={`market-index-change ${getChangeClass(selectedSeries.change_1w_pct)}`}>
						{formatChange(selectedSeries.change_1w_pct)}
					</strong>
				</div>
				<div>
					<span>1M</span>
					<strong className={`market-index-change ${getChangeClass(selectedSeries.change_1m_pct)}`}>
						{formatChange(selectedSeries.change_1m_pct)}
					</strong>
				</div>
			</div>

			<div className="market-index-chart-wrap">
				<svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} role="img" className="market-index-chart">
					<line x1={CHART_PADDING} y1={CHART_PADDING} x2={CHART_PADDING} y2={CHART_HEIGHT - CHART_PADDING} />
					<line x1={CHART_PADDING} y1={CHART_HEIGHT - CHART_PADDING} x2={CHART_WIDTH - CHART_PADDING} y2={CHART_HEIGHT - CHART_PADDING} />
					<text x={CHART_PADDING + 2} y={CHART_PADDING - 8}>{formatValue(chartData.max)}</text>
					<text x={CHART_PADDING + 2} y={CHART_HEIGHT - 6}>{formatValue(chartData.min)}</text>
					<path d={chartData.path} style={{ stroke: selectedColor }} />
				</svg>
				<div className="market-index-axis">
					<span>{formatDate(chartData.firstDate)}</span>
					<span>{formatDate(chartData.lastDate)}</span>
				</div>
			</div>

			<table className="market-index-table">
				<tbody>
					<tr>
						<th>Index</th>
						<th>Date</th>
						<th>Value</th>
						<th>1W</th>
						<th>1M</th>
					</tr>
					{latestRows.map((item, index) => (
						<tr key={item.name}>
							<td>
								<span className="market-index-swatch" style={{ backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length] }} />
								{getLabel(item.name)}
							</td>
							<td>{formatDate(item.latest_date || item.latest.date)}</td>
							<td>{formatValue(item.latest_value ?? item.latest.value)}</td>
							<td className={`market-index-change ${getChangeClass(item.change_1w_pct)}`}>{formatChange(item.change_1w_pct)}</td>
							<td className={`market-index-change ${getChangeClass(item.change_1m_pct)}`}>{formatChange(item.change_1m_pct)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};